/**
 * Convert an RCT3 park/track .dat TrackPiece chain into an RCSim coaster file.
 *
 * Banking: megacoaster pieces do not store a numeric bank. Recovered from
 * SYMBOLNAME (+ Reversed). Mirror: RCT3 park XZ → our Y-up frame (flip Z only).
 */

import { CoasterFile, CURRENT_VERSION, FORMAT, parseCoasterFile } from '../doc/schema';
import { parseArchive, Rct3Object, Rct3Value } from './parse';

const TILE_M = 4.0;
const MIRROR_Z = true;
const BANK_TILT = 40.0;
const BANK_90 = 90.0;
const BANK_OVER = 121.0;

type Vec3 = [number, number, number];

function asObj(v: Rct3Value | undefined): Record<string, Rct3Value> | null {
  if (v && typeof v === 'object' && !Array.isArray(v) && !('_opaque' in v) && !('_unknown' in v)) {
    return v as Record<string, Rct3Value>;
  }
  return null;
}

function asStr(v: Rct3Value | undefined): string | null {
  return typeof v === 'string' ? v : null;
}

function asNum(v: Rct3Value | undefined, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function asBool(v: Rct3Value | undefined): boolean {
  return v === true;
}

function dist(a: Vec3, b: Vec3): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return Math.hypot(dx, dy, dz);
}

function copysign(mag: number, sign: number): number {
  return Math.sign(sign) === -1 || Object.is(sign, -0) ? -Math.abs(mag) : Math.abs(mag);
}

function stripSymbol(name: string | null): string {
  if (!name) return '';
  return name.toLowerCase().split(':')[0];
}

function hasExplicitSide(token: string): boolean {
  return token.includes('left') || token.includes('right');
}

function stateBank(token: string): number {
  const t = token.toLowerCase();
  const sign = t.includes('left') ? -1.0 : 1.0;
  if (t.includes('overbank')) return sign * BANK_OVER;
  if (t.includes('tilt90')) return sign * BANK_90;
  if (t.includes('tilt')) return sign * BANK_TILT;
  return 0.0;
}

function splitTransition(n: string): [string, string] | null {
  let start = 0;
  while (start < n.length && n[start] >= '0' && n[start] <= '9') start++;
  const idx = n.indexOf('2', start);
  if (idx <= 0 || idx >= n.length - 1) return null;
  const left = n.slice(0, idx);
  const right = n.slice(idx + 1);
  if (left && right) return [left, right];
  return null;
}

function pieceEntryExitBank(name: string | null): Vec3 {
  const n = stripSymbol(name);
  if (!n) return [0, 0, 0];

  if (n.includes('overbankedturn')) {
    const sign = n.includes('left') ? -1.0 : 1.0;
    return [0.0, sign * BANK_90, sign * BANK_OVER];
  }

  const parts = splitTransition(n);
  if (parts) return [stateBank(parts[0]), stateBank(parts[1]), 0.0];

  const b = stateBank(n);
  return [b, b, 0.0];
}

function travelEntryExit(name: string | null, reversed: boolean): Vec3 {
  const [entry, exit, mid] = pieceEntryExitBank(name);
  if (reversed) return [-exit, -entry, -mid];
  return [entry, exit, mid];
}

function isUnsignedBanked(name: string | null): boolean {
  const n = stripSymbol(name);
  if (!n || hasExplicitSide(n)) return false;
  if (n.includes('overbankedturn')) return false;
  return n.includes('tilt') || n.includes('overbank');
}

function chaikin(points: Vec3[], rounds = 2): Vec3[] {
  let pts = points.slice();
  for (let r = 0; r < rounds; r++) {
    const out: Vec3[] = [pts[0]];
    for (let i = 0; i < pts.length - 1; i++) {
      const p = pts[i];
      const q = pts[i + 1];
      out.push([0.75 * p[0] + 0.25 * q[0], 0.75 * p[1] + 0.25 * q[1], 0.75 * p[2] + 0.25 * q[2]]);
      out.push([0.25 * p[0] + 0.75 * q[0], 0.25 * p[1] + 0.75 * q[1], 0.25 * p[2] + 0.75 * q[2]]);
    }
    out.push(pts[pts.length - 1]);
    pts = out;
  }
  return pts;
}

function nodesFromSamples(points: Vec3[], nodeEvery: number) {
  const idxs: number[] = [];
  for (let i = 0; i < points.length; i += nodeEvery) idxs.push(i);
  if (idxs[idxs.length - 1] !== points.length - 1) idxs.push(points.length - 1);

  return idxs.map((i, k) => {
    const p = points[i];
    const prev = points[Math.max(0, i - 1)];
    const nxt = points[Math.min(points.length - 1, i + 1)];
    let tan: Vec3 = [nxt[0] - prev[0], nxt[1] - prev[1], nxt[2] - prev[2]];
    const tlen = Math.hypot(tan[0], tan[1], tan[2]) || 1.0;
    tan = [tan[0] / tlen, tan[1] / tlen, tan[2] / tlen];
    const dNext =
      k + 1 < idxs.length ? dist(p, points[idxs[k + 1]]) : dist(p, prev) * nodeEvery;
    const dPrev = k > 0 ? dist(p, points[idxs[k - 1]]) : dNext;
    return {
      id: `n${k}`,
      p: [p[0], p[1], p[2]] as [number, number, number],
      hIn: [-tan[0] * dPrev / 3, -tan[1] * dPrev / 3, -tan[2] * dPrev / 3] as [number, number, number],
      hOut: [tan[0] * dNext / 3, tan[1] * dNext / 3, tan[2] * dNext / 3] as [number, number, number],
      mode: 'smooth' as const,
    };
  });
}

function orderTrackChain(pieces: Rct3Object[]): Rct3Object[] {
  const byRef = new Map<number, Rct3Object>();
  for (const p of pieces) byRef.set(p._ref, p);

  const adj = new Map<number, Set<number>>();
  const addEdge = (a: number, b: number) => {
    if (!adj.has(a)) adj.set(a, new Set());
    if (!adj.has(b)) adj.set(b, new Set());
    adj.get(a)!.add(b);
    adj.get(b)!.add(a);
  };
  for (const p of pieces) {
    for (const k of ['Next', 'Prev'] as const) {
      const t = asNum(p[k]);
      if (byRef.has(t)) addEdge(p._ref, t);
    }
  }

  const seen = new Set<number>();
  const comps: Rct3Object[][] = [];
  for (const p of pieces) {
    if (seen.has(p._ref)) continue;
    const stack = [p._ref];
    const comp: Rct3Object[] = [];
    while (stack.length) {
      const ref = stack.pop()!;
      if (seen.has(ref)) continue;
      seen.add(ref);
      const piece = byRef.get(ref);
      if (!piece) continue;
      comp.push(piece);
      for (const n of adj.get(ref) ?? []) {
        if (!seen.has(n)) stack.push(n);
      }
    }
    comps.push(comp);
  }
  comps.sort((a, b) => b.length - a.length);
  if (!comps.length) throw new Error('No TrackPiece objects found in RCT3 file');
  const goliath = comps[0];
  const grefs = new Set(goliath.map((p) => p._ref));

  const stations = goliath.filter((p) => {
    const name = asStr(p.SYMBOLNAME);
    return name !== null && name.toLowerCase().includes('station');
  });
  if (!stations.length) throw new Error('No station pieces in largest track component');

  const stationKey = (p: Rct3Object): [number, number, number] => {
    const sdf = asObj(p.SceneryItemDataField) ?? {};
    const name = asStr(p.SYMBOLNAME) ?? '';
    const sd = asNum(p.StartDistance);
    return [name.includes('Stationmiddle') ? 0 : 1, Math.abs(sd), -(asNum(sdf.POSX))];
  };
  stations.sort((a, b) => {
    const ka = stationKey(a);
    const kb = stationKey(b);
    return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2];
  });
  const start = stations[0];

  const chain: Rct3Object[] = [];
  let cur: Rct3Object | undefined = start;
  const seen2 = new Set<number>();
  while (cur && !seen2.has(cur._ref)) {
    seen2.add(cur._ref);
    chain.push(cur);
    const nxt = asNum(cur.Next);
    if (!grefs.has(nxt)) break;
    cur = byRef.get(nxt);
    if (chain.length > goliath.length + 2) break;
  }
  return chain;
}

function piecePos(p: Rct3Object): Vec3 {
  const sdf = asObj(p.SceneryItemDataField) ?? {};
  return [asNum(sdf.POSX) * TILE_M, asNum(sdf.HEIGHT), asNum(sdf.POSZ) * TILE_M];
}

function continuousBanks(chain: Rct3Object[]): Vec3[] {
  const out: Vec3[] = [];
  let prevExit = 0.0;
  for (const p of chain) {
    const name = asStr(p.SYMBOLNAME);
    const rev = asBool(p.Reversed);
    let [entry, exit, mid] = travelEntryExit(name, rev);

    if (isUnsignedBanked(name) && prevExit !== 0.0) {
      const magE = entry !== 0.0 ? Math.abs(entry) : Math.abs(prevExit);
      const magX = exit !== 0.0 ? Math.abs(exit) : magE;
      entry = copysign(magE, prevExit);
      exit = copysign(magX, prevExit);
      if (mid !== 0.0) mid = copysign(Math.abs(mid), prevExit);
    }

    if (Math.abs(entry - prevExit) < 0.5) entry = prevExit;

    out.push([entry, exit, mid]);
    prevExit = exit;
  }
  return out;
}

interface PieceMeta {
  s: number;
  name: string | null;
  entry: number;
  exit: number;
  mid: number;
  reversed: boolean;
  brake: boolean;
  station: boolean;
}

function buildRollKeys(
  meta: PieceMeta[],
  sAcc: number,
  mapS: (s: number) => number,
): { s: number; deg: number; interp: 'smooth' }[] {
  const maxRate = 8.0;
  const targets: [number, number][] = [[0.0, 0.0]];
  for (let i = 0; i < meta.length; i++) {
    const m = meta[i];
    const s0 = mapS(m.s);
    const entry = m.entry;
    const exit = m.exit;
    const mid = m.mid;
    let s1 = i + 1 < meta.length ? mapS(meta[i + 1].s) : sAcc;
    if (s1 <= s0) s1 = Math.min(sAcc, s0 + 4.0);
    targets.push([s0, entry]);
    if (Math.abs(mid) > 0.5) targets.push([0.5 * (s0 + s1), mid]);
    if (
      Math.abs(exit - entry) > 0.5 ||
      Math.abs(mid) > 0.5 ||
      Math.abs(exit) > 0.5 ||
      Math.abs(entry) > 0.5
    ) {
      targets.push([s1, exit]);
    }
  }
  if (Math.abs(targets[targets.length - 1][1]) > 0.5) {
    targets.push([sAcc, 0.0]);
  } else if (targets[targets.length - 1][0] < sAcc - 1) {
    targets.push([sAcc, targets[targets.length - 1][1]]);
  }

  const cleaned: [number, number][] = [targets[0]];
  for (let i = 1; i < targets.length; i++) {
    const [s, deg] = targets[i];
    const last = cleaned[cleaned.length - 1];
    if (Math.abs(s - last[0]) < 0.2 && Math.abs(deg - last[1]) < 0.5) {
      cleaned[cleaned.length - 1] = [s, deg];
    } else {
      cleaned.push([s, deg]);
    }
  }

  const keys: [number, number][] = [[0.0, 0.0]];
  let curS = 0.0;
  let curDeg = 0.0;

  const appendRamp = (toS: number, toDeg: number) => {
    if (Math.abs(toDeg - curDeg) < 0.5) {
      curS = Math.max(curS, toS);
      return;
    }
    const need = Math.abs(toDeg - curDeg) / maxRate;
    let s1 = Math.max(toS, curS + need);
    let s0 = s1 - need;
    if (s0 < curS + 0.05) {
      s0 = curS + 0.05;
      s1 = s0 + need;
    }
    if (s1 > sAcc) {
      s1 = sAcc;
      s0 = Math.max(curS + 0.05, s1 - need);
    }
    keys.push([s0, curDeg]);
    keys.push([s1, toDeg]);
    curS = s1;
    curDeg = toDeg;
  };

  const shortestDelta = (frm: number, to: number) => ((to - frm + 180.0) % 360.0) - 180.0;

  for (let i = 1; i < cleaned.length; i++) {
    const [s, deg] = cleaned[i];
    appendRamp(s, curDeg + shortestDelta(curDeg, deg));
  }

  if (keys[keys.length - 1][0] < sAcc - 0.5) {
    keys.push([sAcc, keys[keys.length - 1][1]]);
  }

  const out: { s: number; deg: number; interp: 'smooth' }[] = [];
  for (const [rawS, deg] of keys) {
    const s = Math.min(Math.max(rawS, 0.0), sAcc);
    if (out.length && Math.abs(out[out.length - 1].s - s) < 0.15) {
      out[out.length - 1] = { s, deg, interp: 'smooth' };
    } else {
      out.push({ s, deg, interp: 'smooth' });
    }
  }
  return out;
}

function pickRide(rides: Rct3Object[]): Rct3Object | undefined {
  if (!rides.length) return undefined;
  const named = rides.find((r) => asStr(r.Name));
  return named ?? rides[0];
}

export interface Rct3ConvertOptions {
  /** Source filename for meta.author / fallback name. */
  sourceName?: string;
}

/** Convert RCT3 .dat bytes into an RCSim coaster file object. */
export function convertRct3Dat(data: Uint8Array, options: Rct3ConvertOptions = {}): CoasterFile {
  const { blocks } = parseArchive(data);
  const pieces = blocks.filter((b) => b._class === 'TrackPiece');
  const rides = blocks.filter((b) => b._class === 'TrackedRideInstance');
  if (!pieces.length) throw new Error('No TrackPiece data found — is this an RCT3 park/track .dat?');

  const chain = orderTrackChain(pieces);
  const ride = pickRide(rides);
  const liftSpeed = asNum(ride?.LiftChainSpeed, 6.5);
  const nCars = Math.min(12, Math.max(1, Math.round(asNum(ride?.NCarsPerTrain, 8))));

  const rawPts = chain.map(piecePos);
  const banks = continuousBanks(chain);
  const ys = rawPts.map((p) => p[1]);
  const ymin = Math.min(...ys);
  const ySpan = Math.max(...ys) - ymin;
  const scaleY = ySpan > 1 ? 46.8 / ySpan : 1.0;
  const cx = rawPts.reduce((s, p) => s + p[0], 0) / rawPts.length;
  const cz = rawPts.reduce((s, p) => s + p[2], 0) / rawPts.length;

  const points: Vec3[] = [];
  const meta: PieceMeta[] = [];
  let sAcc = 0.0;
  let prev: Vec3 | null = null;

  for (let i = 0; i < chain.length; i++) {
    const p = chain[i];
    const raw = rawPts[i];
    const [entry, exit, mid] = banks[i];
    let x = raw[0] - cx;
    let z = raw[2] - cz;
    if (MIRROR_Z) z = -z;
    const y = (raw[1] - ymin) * scaleY + 6.0;
    const pt: Vec3 = [x, y, z];
    if (prev) sAcc += dist(prev, pt);
    const name = asStr(p.SYMBOLNAME);
    meta.push({
      s: sAcc,
      name,
      entry,
      exit,
      mid,
      reversed: asBool(p.Reversed),
      brake: !!name && name.toLowerCase().includes('brake'),
      station: !!name && name.toLowerCase().includes('station'),
    });
    points.push(pt);
    prev = pt;
  }

  const smoothPts = chaikin(points, 2);
  const smoothS = [0.0];
  for (let i = 1; i < smoothPts.length; i++) {
    smoothS.push(smoothS[i - 1] + dist(smoothPts[i - 1], smoothPts[i]));
  }
  sAcc = smoothS[smoothS.length - 1];
  const nodes = nodesFromSamples(smoothPts, Math.max(2, Math.floor(smoothPts.length / 90)));

  const mapS = (sRaw: number): number => {
    if (!meta.length || meta[meta.length - 1].s <= 0) return sRaw;
    return (sRaw / meta[meta.length - 1].s) * sAcc;
  };

  const rollKeys = buildRollKeys(meta, sAcc, mapS);

  const sections: CoasterFile['track']['sections'] = [];
  const stationIdxs = meta.map((m, i) => (m.station ? i : -1)).filter((i) => i >= 0);
  if (stationIdxs.length) {
    sections.push({
      kind: 'station',
      from: mapS(meta[stationIdxs[0]].s),
      to: Math.min(sAcc, mapS(meta[stationIdxs[stationIdxs.length - 1]].s) + 4.0),
    });
  }
  const brakeIdxs = meta.map((m, i) => (m.brake ? i : -1)).filter((i) => i >= 0);
  if (brakeIdxs.length) {
    const runs: number[][] = [];
    let run = [brakeIdxs[0]];
    for (let i = 1; i < brakeIdxs.length; i++) {
      const idx = brakeIdxs[i];
      if (idx === run[run.length - 1] + 1) run.push(idx);
      else {
        runs.push(run);
        run = [idx];
      }
    }
    runs.push(run);
    const stS = sections[0]?.from ?? sAcc;
    const pre = runs.filter(
      (r) => mapS(meta[r[r.length - 1]].s) <= stS + 1 || meta[r[r.length - 1]].s > meta[meta.length - 1].s * 0.7,
    );
    const chosen = pre.length ? pre[pre.length - 1] : runs[runs.length - 1];
    sections.push({
      kind: 'brake',
      from: Math.min(sAcc - 1.0, mapS(meta[chosen[0]].s)),
      to: Math.min(sAcc - 0.25, mapS(meta[chosen[chosen.length - 1]].s) + 4.0),
      target: 3.5,
      decel: 5.5,
    });
  }

  const dx = points[1][0] - points[0][0];
  const dz = points[1][2] - points[0][2];
  const heading = Math.atan2(dz, dx);

  const sourceName = options.sourceName ?? 'rct3.dat';
  const rideName = asStr(ride?.Name);
  const displayName = rideName ? `${rideName} (RCT3 import)` : `${stemName(sourceName)} (RCT3 import)`;

  return parseCoasterFile({
    format: FORMAT,
    version: CURRENT_VERSION,
    meta: {
      name: displayName,
      author: `Imported from ${sourceName}`,
      created: new Date().toISOString(),
    },
    settings: {
      gravity: 9.80665,
      friction: 0.011,
      drag: 0.00028,
      minSpeed: Math.max(liftSpeed, 2.0),
    },
    train: { cars: nCars, carMass: 1100, seatsPerCar: 4, carLength: 3.4 },
    start: {
      position: [points[0][0], points[0][1], points[0][2]],
      heading,
    },
    track: {
      closed: true,
      nodes,
      rollKeys,
      sections,
    },
    supportOverrides: [],
  });
}

function stemName(filename: string): string {
  const base = filename.replace(/^.*[/\\]/, '');
  return base.replace(/\.dat$/i, '') || 'RCT3';
}
